import { formatCurrency, formatDate, t } from "../../i18n";
import { AssinaturaStatusResponse } from "../../types/billing.types";

// Regras da faixa de aviso de cobrança (docs/ARQUITETURA_PAGAMENTOS_REFERENCIA.md §19),
// a partir de `billing.aviso`. A faixa em si é app/BillingNotice.tsx.
//
// Inadimplência (backend: services/billing/inadimplencia.py): quem paga vê
// valor, prazos e o botão de pagar; os médicos da clínica nunca veem botão
// de pagamento — só que há uma pendência e que devem falar com o responsável,
// e só a partir de 7 dias de atraso.

type Aviso = AssinaturaStatusResponse["aviso"];
export type NoticeTone = "warning" | "danger";
export type NoticeAction = "pay" | "updateCard" | "reactivate" | "subscribe" | "talkToUs";

export interface Notice {
  text: string;
  tone: NoticeTone;
  action?: NoticeAction;
}

// Pagamento pendente não fecha (§19: "faixa fixa no topo, não fecha"); os
// demais aceitam dispensa (persistida na aba via sessionStorage).
const DISMISSABLE: Partial<Record<Aviso, boolean>> = {
  cancela_em: true,
  trial_termina_em: true,
  trial_encerrado: true,
  expirado: true,
  concessao_termina_em: true,
  concessao_encerrada: true,
};
export const isDismissable = (aviso: Aviso) => Boolean(DISMISSABLE[aviso]);

function payerNotice(billing: AssinaturaStatusResponse): Notice | null {
  const inad = billing.inadimplencia;
  const amount = inad?.valor_em_aberto_centavos ? t("billing.notice.amount", { value: formatCurrency(inad.valor_em_aberto_centavos) }) : "";
  const lateTone: NoticeTone = inad && inad.dias_em_atraso >= 7 ? "danger" : "warning";
  switch (billing.aviso) {
    case "fatura_vencida":
      return {
        text: inad
          ? t("billing.notice.payer.overdue", { amount, since: formatDate(inad.vencida_desde), suspends: formatDate(inad.suspende_em) })
          : t("billing.notice.payer.overdueNoData"),
        tone: lateTone,
        action: "pay",
      };
    case "atualizar_cartao":
      return {
        text: inad
          ? t("billing.notice.payer.cardFailed", { amount, suspends: formatDate(inad.suspende_em) })
          : t("billing.notice.payer.cardFailedNoData"),
        tone: lateTone,
        action: "updateCard",
      };
    case "acesso_suspenso":
      return {
        text: inad
          ? t("billing.notice.payer.suspended", { amount, cancels: formatDate(inad.cancela_em) })
          : t("billing.notice.payer.suspendedNoData"),
        tone: "danger",
        action: "pay",
      };
    case "encerrado_inadimplencia":
      return { text: t("billing.notice.payer.closedDebt"), tone: "danger" };
    case "cancela_em":
      return {
        text: billing.plano?.proxima_cobranca
          ? t("billing.notice.payer.cancelsOn", { date: formatDate(billing.plano.proxima_cobranca) })
          : t("billing.notice.payer.cancelsNoDate"),
        tone: "warning",
        action: "reactivate",
      };
    case "trial_termina_em":
      // `acesso_ate` tem 3 dias de margem; a data certa da 1ª cobrança é `trial_fim`.
      return {
        text: billing.plano?.trial_fim
          ? t("billing.notice.payer.trialEndsOn", { date: formatDate(billing.plano.trial_fim) })
          : t("billing.notice.payer.trialNoDate"),
        tone: "warning",
      };
    case "trial_pagamento_pendente":
      return {
        text: inad
          ? t("billing.notice.payer.trialPaymentPending", { amount, cancels: formatDate(inad.cancela_em) })
          : t("billing.notice.payer.trialPaymentPendingNoData"),
        tone: "danger",
        action: "pay",
      };
    case "trial_encerrado":
      return { text: t("billing.notice.payer.trialEnded"), tone: "danger", action: "subscribe" };
    case "expirado":
      return { text: t("billing.notice.payer.expired"), tone: "danger", action: "subscribe" };
    case "concessao_termina_em":
      return {
        text: billing.acesso_ate
          ? t("billing.notice.payer.grantEndsOn", { date: formatDate(billing.acesso_ate) })
          : t("billing.notice.payer.grantEndsNoDate"),
        tone: "warning",
        action: "subscribe",
      };
    case "concessao_encerrada":
      return { text: t("billing.notice.payer.grantEnded"), tone: "danger", action: "subscribe" };
    default:
      return null;
  }
}

/**
 * Dono da clínica: o plano é negociado com a LetsDoc — nunca "Assinar" nem o
 * Portal para reativar. Casos de pagamento pendente (fatura vencida,
 * suspensão) seguem `payerNotice`, que manda pagar a fatura.
 */
function ownerNotice(billing: AssinaturaStatusResponse): Notice | null {
  switch (billing.aviso) {
    case "concessao_termina_em":
      return {
        text: billing.acesso_ate
          ? t("billing.notice.owner.trialEndsOn", { date: formatDate(billing.acesso_ate) })
          : t("billing.notice.owner.trialEndsNoDate"),
        tone: "warning",
        action: "talkToUs",
      };
    case "concessao_encerrada":
      return { text: t("billing.notice.owner.trialEnded"), tone: "danger", action: "talkToUs" };
    case "cancela_em":
      return {
        text: billing.plano?.proxima_cobranca
          ? t("billing.notice.owner.cancelsOn", { date: formatDate(billing.plano.proxima_cobranca) })
          : t("billing.notice.owner.cancelsNoDate"),
        tone: "warning",
        action: "talkToUs",
      };
    case "encerrado_inadimplencia":
      return { text: t("billing.notice.owner.closedDebt"), tone: "danger", action: "talkToUs" };
    case "expirado":
      return { text: t("billing.notice.owner.expired"), tone: "danger", action: "talkToUs" };
    default:
      return null;
  }
}

/** Médico da clínica (ou secretária): sem valores e sem botão de pagar. */
function memberNotice(billing: AssinaturaStatusResponse): Notice | null {
  const inad = billing.inadimplencia;
  switch (billing.aviso) {
    case "fatura_vencida":
    case "atualizar_cartao":
      // `dias_em_atraso` vem do backend (respeita o Test Clock no sandbox).
      if (!inad || inad.dias_em_atraso < 7) return null;
      return { text: t("billing.notice.member.pending", { date: formatDate(inad.suspende_em) }), tone: "warning" };
    case "acesso_suspenso":
      return { text: t("billing.notice.member.suspended"), tone: "danger" };
    case "encerrado_inadimplencia":
      return { text: t("billing.notice.member.closedDebt"), tone: "danger" };
    case "expirado":
    case "concessao_encerrada":
      return { text: t("billing.notice.member.ended"), tone: "danger" };
    default:
      return null;
  }
}

export interface NoticeViewer {
  isClinicOwner: boolean;
  isClinicPlan: boolean;
  canViewBilling: boolean;
  canManagePlan: boolean;
}

/** Aviso que este usuário vê, já sem as ações que o papel dele não permite. */
export function billingNotice(billing: AssinaturaStatusResponse, viewer: NoticeViewer): Notice | null {
  // Dono da clínica (inclusive em acesso experimental, quando `canViewBilling`
  // é falso): mensagens de "fale com a LetsDoc". Demais membros: mensagem
  // própria, nunca botão de pagar.
  const isMemberWithoutBilling = viewer.isClinicPlan && !viewer.canViewBilling && !viewer.isClinicOwner;
  const notice = viewer.isClinicOwner
    ? ownerNotice(billing) ?? payerNotice(billing)
    : isMemberWithoutBilling
      ? memberNotice(billing)
      : payerNotice(billing);
  if (!notice?.action || notice.action === "talkToUs") return notice;
  // "Falar com a LetsDoc" vale para o dono mesmo sem acesso à cobrança; as demais ações não.
  if (!viewer.canViewBilling) return { ...notice, action: undefined };
  if (billing.aviso === "cancela_em" && !viewer.canManagePlan) return { ...notice, action: undefined };
  return notice;
}

const KIT_USAGE = { pacotes_cirurgia: "surgery", pacotes_opme: "opme" } as const;

/** "Pacotes de cirurgia: 2/3" para cada limite do plano. */
export function kitUsageParts(billing: AssinaturaStatusResponse): string[] {
  const limits = billing.limites;
  if (!limits) return [];
  const usage = billing.uso ?? billing.uso_free ?? {};
  return Object.entries(KIT_USAGE)
    .filter(([key]) => limits[key] != null)
    .map(([key, label]) =>
      t("billing.notice.usage.item", { label: t(`billing.notice.usage.${label}`), used: usage[key] ?? 0, max: limits[key]! })
    );
}
